// Tutorial slide 3/5. Interactive mock chat bubble where one character is
// tappable; tapping it reveals a contextual pinyin+meaning popover. Mirrors
// the real chat tap-to-look-up flow (ChatBubble.tsx:63-105) so the first
// time the user does this in a real conversation it already feels familiar.
// No API call — purely local mock data.

import { useState } from 'react';
import { ChevronRight, Hand } from 'lucide-react';
import { C } from '@/styles/tokens';

interface LookupDemoProps {
  onContinue: () => void;
}

const SENTENCE = [
  { hz: '我', py: 'wǒ', en: 'I', taught: false },
  { hz: '喜欢', py: 'xǐhuān', en: 'like', taught: false },
  { hz: '喝', py: 'hē', en: 'drink', taught: false },
  { hz: '咖啡', py: 'kāfēi', en: 'coffee', taught: true },
];

export function LookupDemo({ onContinue }: LookupDemoProps) {
  const [revealed, setRevealed] = useState(false);
  const target = SENTENCE.find((t) => t.taught)!;

  return (
    <div className="flex-1 flex flex-col">
      <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
        See a word you don&apos;t know?
      </div>
      <div className="ff-body text-[14px] leading-relaxed mb-8" style={{ color: C.inkLight }}>
        Tap the character. Your friend will tell you what it means — without breaking the conversation.
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div
          className="relative w-full max-w-[320px] p-4 rounded-2xl anim-scale-in"
          style={{
            background: 'white',
            border: `1px solid ${C.mist}`,
            boxShadow: '0 6px 18px rgba(26,22,20,0.06)',
          }}
        >
          <div className="ff-body text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: C.inkSofter }}>
            小美
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1">
            {SENTENCE.map((t) => {
              if (t.taught) {
                return (
                  <span key={t.hz} className="relative inline-flex">
                    {!revealed && (
                      <span
                        aria-hidden
                        className="absolute -top-7 left-1/2 -translate-x-1/2 anim-bounce-dot"
                        style={{ color: C.vermillion }}
                      >
                        <Hand size={20} />
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setRevealed(true)}
                      className={`ff-zh-serif text-2xl leading-tight rounded-md px-1 transition-colors ${
                        revealed ? '' : 'anim-pulse-glow'
                      }`}
                      style={{
                        color: revealed ? C.vermillion : C.ink,
                        background: revealed ? C.blush : 'transparent',
                        borderBottom: `2px dashed ${revealed ? C.vermillion : C.gold}`,
                      }}
                      aria-label={`Look up ${t.hz}`}
                      aria-expanded={revealed}
                    >
                      {t.hz}
                    </button>
                  </span>
                );
              }
              return (
                <span key={t.hz} className="ff-zh-serif text-2xl leading-tight" style={{ color: C.ink }}>
                  {t.hz}
                </span>
              );
            })}
          </div>
          <div className="ff-body text-[12px] italic" style={{ color: C.inkLight }}>
            {SENTENCE.map((t) => t.py).join(' ')}
          </div>

          {revealed && (
            <div
              className="mt-3 p-3 rounded-xl anim-slide-up"
              style={{ background: C.blush, border: `1px solid ${C.coral}` }}
            >
              <div className="flex items-baseline gap-2">
                <span className="ff-zh-serif text-xl font-semibold" style={{ color: C.vermillionDeep }}>
                  {target.hz}
                </span>
                <span className="ff-body text-[12px] italic" style={{ color: C.inkLight }}>
                  {target.py}
                </span>
              </div>
              <div className="ff-body text-[13px] mt-0.5" style={{ color: C.ink }}>
                {target.en}
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!revealed}
        className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
        style={{ background: C.vermillion }}
      >
        {revealed ? 'Then keep it' : `Try tapping ${target.hz}`}
        {revealed && <ChevronRight size={16} aria-hidden />}
      </button>
    </div>
  );
}
